import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft, ArrowRight, Monitor, Lock, KeyRound, Settings, Shield,
  Rocket, RefreshCw, Briefcase, Users, CheckCircle2, Laptop,
} from "lucide-react";
import { motion } from "framer-motion";
import windowsHeroBg from "@/assets/windows-hero-bg.jpg";
import microsoftLogo from "@/assets/ms-icons/microsoft.svg";
import onedriveIcon from "@/assets/ms-icons/onedrive.svg";
import sharepointIcon from "@/assets/ms-icons/sharepoint.svg";
import defenderIcon from "@/assets/ms-icons/defender.svg";

const voordelen = [
  {
    icon: Lock,
    title: "Automatische beveiliging met BitLocker",
    text: "Schijven worden automatisch versleuteld. Je bedrijfsdata is direct beschermd tegen diefstal of verlies. Herstelsleutels worden veilig opgeslagen in Microsoft Entra ID.",
  },
  {
    icon: KeyRound,
    title: "Single Sign-On voor Microsoft 365",
    text: "Medewerkers loggen één keer in en zijn direct aangemeld in Outlook, Teams, OneDrive, SharePoint en Edge. Minder wachtwoordproblemen, minder IT-tickets.",
  },
  {
    icon: Settings,
    title: "Automatische installatie van Office-apps",
    text: "Word, Excel, Outlook en Teams worden automatisch geïnstalleerd. Geen installatietijd of handmatige downloads. Altijd de juiste versie en instellingen.",
  },
  {
    icon: onedriveIcon,
    title: "OneDrive automatisch ingericht",
    text: "OneDrive wordt automatisch ingelogd en gesynchroniseerd. Bureaublad, Documenten, Afbeeldingen én Downloads worden standaard geback-upt. Na een device-swap zie je direct al je bestanden.",
  },
  {
    icon: sharepointIcon,
    title: "SharePoint direct in Verkenner",
    text: "Bedrijfsdocumenten verschijnen automatisch als mappen in de Windows Verkenner. Geen handmatige koppelingen of sync-problemen. Altijd up-to-date.",
  },
  {
    icon: defenderIcon,
    title: "Microsoft Defender Firewall automatisch geconfigureerd",
    text: "Firewall staat standaard aan volgens best-practice configuratie. Bedreigingen worden automatisch geblokkeerd. Medewerkers kunnen instellingen niet per ongeluk uitschakelen.",
  },
  {
    icon: Rocket,
    title: "Nieuwe devices binnen minuten inzetbaar",
    text: "Een medewerker hoeft alleen in te loggen met zijn bedrijfsaccount. Alle apps, instellingen en beveiliging worden automatisch toegepast. Ideaal voor thuiswerken en hybride teams.",
  },
  {
    icon: RefreshCw,
    title: "Consistente en foutloze inrichting",
    text: "Geen fouten door handmatige installatie. Iedere laptop wordt identiek en volgens beleid ingericht. Veel minder beheerwerk voor IT.",
  },
  {
    icon: Briefcase,
    title: "Minder beheer, lagere kosten",
    text: "Minder tickets over wachtwoorden, software en synchronisatie. Nieuwe medewerkers kunnen direct beginnen. IT beheert alles centraal — zelfs als medewerkers thuis werken.",
  },
  {
    icon: Users,
    title: "Eén apparaat voor meerdere medewerkers",
    text: "Elk ingericht Windows-apparaat kan probleemloos door meerdere gebruikers worden gebruikt. Een medewerker logt in met zijn eigen account en alle instellingen worden automatisch geladen. Ideaal bij personeelswisselingen.",
  },
];

const WerkplekWindows = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[480px] overflow-hidden">
        <div className="absolute inset-0">
          <img loading="eager" decoding="async" fetchPriority="high" src={windowsHeroBg} alt="IT-beheerder beheert Windows-apparaten" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(210,25%,12%)]/90 via-[hsl(210,30%,18%)]/80 to-[hsl(204,60%,30%)]/70" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.1),transparent_60%)]" />
        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 p-2.5"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <img loading="lazy" decoding="async" src={microsoftLogo} alt="Microsoft" className="h-8 w-8" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Microsoft Windows{" "}
              <span className="text-white/90">beheer</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Windows-laptops en -pc's worden via Microsoft Intune volledig automatisch ingericht, beveiligd en klaar voor gebruik — binnen enkele minuten. Inloggen en aan de slag.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Automatisch ingericht via Intune
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                BitLocker-versleuteling standaard
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Single Sign-On voor Microsoft 365
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Voordelen van automatisch inrichten met{" "}
              <span className="text-gradient">Microsoft Intune</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Wanneer je organisatie nieuwe laptops of pc's inzet, wil je dat medewerkers zonder gedoe direct aan het werk kunnen. Met Intune wordt alles automatisch geregeld.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-2">
            {voordelen.map((item, i) => {
              const isImageIcon = typeof item.icon === "string";
              const colors = ["bg-hero-gradient", "bg-rinqel-blue-gradient", "bg-rinqel-green-gradient"];
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  {isImageIcon ? (
                    <img loading="lazy" decoding="async" src={item.icon as string} alt="" className="h-8 w-8 shrink-0" />
                  ) : (
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${colors[i % 3]} text-primary-foreground`}>
                      {(() => { const Icon = item.icon as React.ComponentType<{ className?: string }>; return <Icon className="h-5 w-5" />; })()}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wissen op afstand */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rinqel-blue-gradient text-primary-foreground">
              <Shield className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Op afstand <span className="text-gradient">wissen</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Laptop kwijt of gestolen? Een medewerker gaat uit dienst? Geen probleem. Via Intune kunnen we elk apparaat op afstand volledig wissen, zodat alle bedrijfsdata veilig wordt verwijderd. Snel, veilig en zonder gedoe.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MKB-samenvatting */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Perfect voor MKB-bedrijven die willen{" "}
              <span className="text-gradient">professionaliseren</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Intune zorgt voor een veilige, moderne werkplek: automatisch, centraal beheerd en altijd up-to-date. Ideaal voor bedrijven die willen besparen op IT-kosten en toch enterprise-niveau beveiliging willen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Vraag aan" trackLocation="werkplek-windows-cta">
                Vraag aan
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default WerkplekWindows;
